import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'next-i18next';
import { DialogClose, DialogPortal, DialogRoot } from '../../../baseComponents/dialog';
import type { CartDiscountItem } from '../../../codegen/types';
import { useLocaleContext } from '../../../context';
import { useCartContext } from '../../../context/cartContext';
import { TypographyStyles } from '../../../stylings/typography';
import { formatAmountWithCurrency } from '../../../utils/currencyHelper';
import { Locale } from '../../../utils/internationalization';
import { simpleMarkdownTransform } from '../../../utils/transformers';
import { setFreeGiftPopupCookie, shouldShowFreeGiftPopup } from '../freeGiftUtils';
import {
  ColorInfo,
  ConfirmButton,
  Content,
  Description,
  DescriptionContainer,
  DescriptionTitle,
  DetailsColumn,
  DialogContentWrapper,
  DialogOverlay,
  FreeLabel,
  GiftImage,
  GiftTitle,
  ImageContainer,
  OriginalPrice,
  PriceContainer,
  Title,
} from './FreeGiftConfirmationModal.styles';

export function FreeGiftConfirmationModal() {
  const { t } = useTranslation(['lib-global-common']);
  const { cart, setIsPopupOpen } = useCartContext();
  const discountItems = cart?.discountItems;
  const [isOpen, setIsOpen] = useState(false);
  const [currentGift, setCurrentGift] = useState<CartDiscountItem | null>(null);
  const { locale } = useLocaleContext();

  const eligibleGift = useMemo(() => {
    if (!discountItems || discountItems.length === 0) return null;

    return (
      discountItems.find((item) => {
        if (!item || item.isPromoCodeDiscount || item.isFreeGiftOutOfStock) {
          return false;
        }

        return item.lineItemId && shouldShowFreeGiftPopup(item.lineItemId, locale || Locale.DEFAULT_LOCALE);
      }) || null
    );
  }, [discountItems, locale]);

  useEffect(() => {
    if (eligibleGift) {
      setIsOpen(true);
      setIsPopupOpen(true);
      setCurrentGift(eligibleGift);
    } else {
      setIsOpen(false);
      setIsPopupOpen(false);
      setCurrentGift(null);
    }
  }, [eligibleGift, setIsPopupOpen]);

  const saveCookieForCurrentGift = useCallback(() => {
    if (currentGift?.lineItemId) {
      setFreeGiftPopupCookie(currentGift.lineItemId, locale || Locale.DEFAULT_LOCALE);
    }
  }, [currentGift, locale]);

  const handleOpenChange = useCallback(
    async (open: boolean) => {
      if (!open) {
        saveCookieForCurrentGift();
        setIsOpen(false);
        setIsPopupOpen(false);
      }
    },
    [saveCookieForCurrentGift, setIsOpen, setIsPopupOpen],
  );

  const handleClose = useCallback(async () => {
    saveCookieForCurrentGift();
    setIsOpen(false);
    setIsPopupOpen(false);
  }, [saveCookieForCurrentGift, setIsOpen, setIsPopupOpen]);

  if (!currentGift) return null;

  return (
    <DialogRoot open={isOpen} onOpenChange={handleOpenChange}>
      <DialogPortal>
        <div data-focus-lock="cart-modals">
          <DialogOverlay />
          <DialogContentWrapper
            data-testid="freegift-confirmation-modal"
            role="dialog"
            aria-labelledby="freegift-title"
            aria-describedby="freegift-description"
            onEscapeKeyDown={handleClose}
            isWithCloseButton
          >
            <Title id="freegift-title" data-testid="freegift-title">
              {t('free.gift.title')}
            </Title>
            <Content>
              <ImageContainer>
                <GiftImage
                  data-testid="freegift-image"
                  src={currentGift.image}
                  alt={currentGift.title}
                  loading="lazy"
                />
              </ImageContainer>
              <DetailsColumn>
                <GiftTitle data-testid="freegift-name">{currentGift.title}</GiftTitle>
                {currentGift.mediumColourCode && (
                  <ColorInfo data-testid="freegift-colour">
                    {t('free.gift.colour')}: <span>{currentGift.mediumColourCode}</span>
                  </ColorInfo>
                )}
                <PriceContainer data-testid="freegift-price">
                  <OriginalPrice>
                    {formatAmountWithCurrency(currentGift.nowPrice.centAmount, currentGift.nowPrice.currencyCode)}
                  </OriginalPrice>
                  <FreeLabel>{t('free.gift.free.message')}</FreeLabel>
                </PriceContainer>
                <DescriptionTitle id="freegift-description-title">{t('free.gift.description.label')}</DescriptionTitle>
                <DescriptionContainer
                  id="freegift-description"
                  data-testid="freegift-description"
                  aria-labelledby="freegift-description-title"
                >
                  <Description
                    text={simpleMarkdownTransform(currentGift.shortDescription)}
                    fontSize={TypographyStyles.Body.Tiny.Regular}
                  />
                </DescriptionContainer>
              </DetailsColumn>
            </Content>
            <DialogClose asChild>
              <ConfirmButton
                data-testid="freegift-cta"
                aria-label={t('free.gift.ok.button.label')}
                onClick={handleClose}
              >
                {t('free.gift.ok.button')}
              </ConfirmButton>
            </DialogClose>
          </DialogContentWrapper>
        </div>
      </DialogPortal>
    </DialogRoot>
  );
}
